// ProductsPipelinePage.jsx
// ✅ Main page background image added
// ✅ Removed ALL borders (no border anywhere)
// ✅ Clean white cards + good spacing

import * as React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Chip,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

const PAGE_BG =
  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1800&q=60";

const PRODUCTS = [
  {
    id: "fludee",
    name: "FLUDEE",
    tag: "Flagship",
    status: "Active Development",
    trl: 6,
    summary:
      "A next-gen medtech device + platform focused on improving clinical workflow and patient monitoring outcomes.",
    problem:
      "Hospitals face delays and variability in monitoring and decision-making due to fragmented systems and manual processes.",
    innovation: [
      "Smart sensing + data pipeline for real-time insights",
      "Workflow-friendly design to reduce clinical burden",
      "Interoperable architecture for hospital environments",
    ],
    users: ["Clinicians", "Nursing staff", "Hospital admin teams", "Biomedical engineers"],
    roadmap: [
      "Prototype refinement (design freeze + manufacturability)",
      "Pilot deployment (hospital pilot + validation)",
      "Clinical validation (multi-site + governance)",
      "Regulatory pathway (QMS + documentation)",
      "Scale & launch (manufacturing + partnerships)",
    ],
  },
  {
    id: "carelink",
    name: "CareLink Remote Monitoring",
    tag: "Pipeline",
    status: "In Research",
    trl: 4,
    summary: "Remote patient monitoring module for post-discharge follow-up and early risk detection.",
    problem:
      "Post-discharge patients often lack continuous monitoring, leading to late detection of complications and readmissions.",
    innovation: ["Risk scoring from vital trends", "Low-friction patient onboarding", "Clinician dashboard for actionable alerts"],
    users: ["Patients", "Care coordinators", "Clinicians"],
    roadmap: ["Feasibility studies", "Prototype build", "Pilot program", "Integration"],
  },
  {
    id: "steriguard",
    name: "SteriGuard QA Toolkit",
    tag: "Pipeline",
    status: "Concept",
    trl: 3,
    summary: "A QA toolkit to improve device maintenance, calibration, and traceability in hospitals.",
    problem: "Device downtime and inconsistent maintenance logs reduce safety and increase operating costs.",
    innovation: ["Standardized QA workflows + audit-ready reporting", "Quick checks for biomedical teams", "Traceability for history"],
    users: ["Biomedical engineers", "QA teams", "Hospital operations"],
    roadmap: ["Concept validation", "Proof-of-concept", "Partner pilots"],
  },
];

function BulletList({ items }) {
  return (
    <List dense sx={{ py: 0 }}>
      {items.map((t, i) => (
        <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
          <ListItemIcon sx={{ minWidth: 26, color: "success.main" }}>
            <CheckCircleOutlineRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t} primaryTypographyProps={{ sx: { fontSize: 14 } }} />
        </ListItem>
      ))}
    </List>
  );
}

function CardBox({ children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.6 },
        borderRadius: 3,
        bgcolor: "#fff",
        boxShadow: "0 10px 26px rgba(0,0,0,0.10)", // ✅ shadow only (no border)
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function ProductCard({ p }) {
  const trlPct = Math.round(((Math.max(1, Math.min(9, p.trl)) - 1) / 8) * 100);

  return (
    <CardBox>
      <Stack spacing={1.4}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 20, sm: 24 } }}>
              {p.name}
            </Typography>
            <Typography sx={{ color: "text.secondary", mt: 0.3, fontSize: 14 }}>
              {p.summary}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Chip size="small" label={p.tag} />
            <Chip size="small" variant="outlined" color="primary" label={p.status} />
            <Chip size="small" icon={<TimelineOutlinedIcon />} label={`TRL ${p.trl}`} />
          </Stack>
        </Stack>

        <Divider sx={{ opacity: 0.35 }} />

        {/* 3 boxes */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <CardBox sx={{ boxShadow: "0 8px 18px rgba(0,0,0,0.08)" }}>
              <Stack spacing={0.8}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ReportProblemOutlinedIcon fontSize="small" />
                  <Typography sx={{ fontWeight: 800, fontSize: 15 }}>Problem</Typography>
                </Stack>
                <Typography sx={{ color: "text.secondary", fontSize: 14 }}>{p.problem}</Typography>
              </Stack>
            </CardBox>
          </Grid>

          <Grid item xs={12} md={4}>
            <CardBox sx={{ boxShadow: "0 8px 18px rgba(0,0,0,0.08)" }}>
              <Stack spacing={0.8}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LightbulbOutlinedIcon fontSize="small" />
                  <Typography sx={{ fontWeight: 800, fontSize: 15 }}>Innovation</Typography>
                </Stack>
                <BulletList items={p.innovation} />
              </Stack>
            </CardBox>
          </Grid>

          <Grid item xs={12} md={4}>
            <CardBox sx={{ boxShadow: "0 8px 18px rgba(0,0,0,0.08)" }}>
              <Stack spacing={0.8}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <GroupsOutlinedIcon fontSize="small" />
                  <Typography sx={{ fontWeight: 800, fontSize: 15 }}>Users</Typography>
                </Stack>
                <BulletList items={p.users} />
              </Stack>
            </CardBox>
          </Grid>

          {/* TRL */}
          <Grid item xs={12}>
            <CardBox sx={{ boxShadow: "0 8px 18px rgba(0,0,0,0.08)" }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TimelineOutlinedIcon fontSize="small" />
                    <Typography sx={{ fontWeight: 800, fontSize: 15 }}>TRL Stage</Typography>
                  </Stack>
                  <Chip size="small" label={`${trlPct}%`} />
                </Stack>
                <LinearProgress variant="determinate" value={trlPct} />
              </Stack>
            </CardBox>
          </Grid>

          {/* Roadmap */}
          <Grid item xs={12}>
            <CardBox sx={{ boxShadow: "0 8px 18px rgba(0,0,0,0.08)" }}>
              <Stack spacing={0.8}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RocketLaunchOutlinedIcon fontSize="small" />
                  <Typography sx={{ fontWeight: 800, fontSize: 15 }}>Roadmap</Typography>
                </Stack>
                <BulletList items={p.roadmap} />
              </Stack>
            </CardBox>
          </Grid>
        </Grid>
      </Stack>
    </CardBox>
  );
}

export default function ProductsPipelinePage() {
  const theme = useTheme();

  return (
    <Box sx={{ minHeight: "100vh", position: "relative", overflow: "hidden" }}>
      {/* ✅ Background Image */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${PAGE_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* ✅ Overlay for readability */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, ${alpha("#ffffff", 0.78)}, ${alpha("#ffffff", 0.72)})`,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, py: { xs: 2.5, sm: 4 } }}>
        {/* Hero */}
        <CardBox sx={{ mb: { xs: 2.2, sm: 3 } }}>
          <Stack spacing={1.2} alignItems="center" textAlign="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <LocalHospitalOutlinedIcon />
              <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, sm: 22 } }}>
                Products & Pipeline
              </Typography>
            </Stack>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 24, sm: 34 } }}>
              Build → Validate → Deploy
            </Typography>
            <Typography sx={{ maxWidth: 820, color: "text.secondary", fontSize: 14 }}>
              What we’re building, who it’s for, TRL stage, and roadmap to clinical validation and scale.
            </Typography>
          </Stack>
        </CardBox>

        {/* Products */}
        <Stack spacing={{ xs: 2.2, sm: 3 }}>
          {PRODUCTS.map((p) => (
            <ProductCard key={p.id} p={p} />
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
